import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { Components } from "react-markdown";
import { useTranslation } from "react-i18next";
import type { RenderOptions } from "./types";

const TABLE_COPY_ID_SEGMENT = "table";
const BUTTON_TYPE = "button";
const COPIED_CLASS_NAME = " copied";
const COPY_ICON_CLASS_NAME = "ti ti-copy";
const DOWNLOAD_ICON_CLASS_NAME = "ti ti-download";
const COPIED_ICON_CLASS_NAME = "ti ti-check";
const EXTERNAL_LINK_TARGET = "_blank";
const EXTERNAL_LINK_REL = "noopener noreferrer";
const LINE_APP_ICON_SRC = "/assets/common/images/line-app-icon.png";

interface MarkdownImageProps {
  src: string;
  alt: string;
  onOpen?: (src: string, alt: string) => void;
  onCopy?: (src: string) => Promise<void>;
  onDownload?: (src: string, filename?: string) => Promise<boolean>;
  canStar?: boolean;
  isStarred?: boolean;
  onToggleStar?: () => void | Promise<void>;
  canSendToLine?: boolean;
  onSendToLine?: () => void | Promise<void>;
}

const MarkdownImage = ({
  src,
  alt,
  onOpen,
  onCopy,
  onDownload,
  canStar = false,
  isStarred = false,
  onToggleStar,
  canSendToLine = false,
  onSendToLine,
}: MarkdownImageProps): React.ReactElement => {
  const { t } = useTranslation();
  const [hasError, setHasError] = React.useState(false);
  const [feedback, setFeedback] = React.useState<"copy" | "download" | "star" | null>(null);
  const feedbackTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    },
    []
  );

  const showFeedback = (action: "copy" | "download" | "star"): void => {
    setFeedback(action);
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), 1600);
  };

  if (hasError || !src) {
    return (
      <div className="restaurant-chatbot-image-unavailable" role="status">
        <i className="ti ti-photo-off" aria-hidden="true" />
        <span>{t("chatbot_image_unavailable")}</span>
      </div>
    );
  }

  return (
    <figure className="restaurant-chatbot-image-block">
      <button
        className="restaurant-chatbot-image-open-button"
        type={BUTTON_TYPE}
        onClick={() => onOpen?.(src, alt)}
      >
        <img src={src} alt={alt} loading="lazy" onError={() => setHasError(true)} />
      </button>
      <div className="restaurant-chatbot-image-action-bar">
        {onCopy ? (
          <button
            className={`restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action${
              feedback === "copy" ? " success" : ""
            }`}
            type={BUTTON_TYPE}
            onClick={() => void onCopy(src).then(() => showFeedback("copy"))}
            aria-label="Copy image"
            data-tooltip={feedback === "copy" ? "Copied!" : "Copy image"}
          >
            <i className={feedback === "copy" ? COPIED_ICON_CLASS_NAME : COPY_ICON_CLASS_NAME} />
          </button>
        ) : null}
        {onDownload ? (
          <button
            className={`restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action${
              feedback === "download" ? " success" : ""
            }`}
            type={BUTTON_TYPE}
            onClick={() =>
              void onDownload(src, alt).then((downloaded) => {
                if (downloaded) showFeedback("download");
              })
            }
            aria-label={t("chatbot_download_image")}
            data-tooltip={feedback === "download" ? t("chatbot_downloaded") : t("chatbot_download_image")}
          >
            <i
              className={feedback === "download" ? COPIED_ICON_CLASS_NAME : DOWNLOAD_ICON_CLASS_NAME}
            />
          </button>
        ) : null}
        {canStar ? (
          <button
            className={`restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action${
              feedback === "star" ? " success" : ""
            }${isStarred ? " active" : ""}`}
            type={BUTTON_TYPE}
            onClick={() =>
              void Promise.resolve(onToggleStar?.()).then(() => {
                showFeedback("star");
              })
            }
            aria-label={isStarred ? t("chatbot_remove_star") : t("chatbot_star_image")}
            data-tooltip={
              feedback === "star"
                ? (isStarred ? t("chatbot_removed_from_starred") : t("chatbot_saved_to_starred"))
                : (isStarred ? t("chatbot_remove_star") : t("chatbot_star_image"))
            }
          >
            <i className={isStarred ? "ti ti-star-filled" : "ti ti-star"} />
          </button>
        ) : null}
        {canSendToLine ? (
          <button
            className="restaurant-chatbot-copy-btn restaurant-chatbot-inline-image-action"
            type={BUTTON_TYPE}
            onClick={() => void Promise.resolve(onSendToLine?.())}
            aria-label="Send to LINE"
            data-tooltip="Send to LINE"
          >
            <img className="restaurant-chatbot-line-icon" src={LINE_APP_ICON_SRC} alt="LINE" />
          </button>
        ) : null}
      </div>
      {alt ? <figcaption>{alt}</figcaption> : null}
    </figure>
  );
};

export const getMessagePlainText = (content: string): string => content.trim();

export const extractMarkdownImageSources = (content: string): Array<{ src: string; alt: string }> => {
  const matches = [...content.matchAll(/!\[([^\]]*)\]\(([^)\s>]+|<[^>]+>)(?:\s+"[^"]*")?\)/g)];
  return matches.map((match) => ({
    alt: (match[1] || "").trim(),
    src: String(match[2] || "").replace(/^<|>$/g, "").trim(),
  })).filter((item) => !!item.src);
};

export const renderMessageContent = (
  content: string,
  options: RenderOptions = {}
): React.ReactNode[] => {
  const {
    onCopyTable,
    onOpenImageViewer,
    onCopyImage,
    onDownloadImage,
    messageId,
    copiedId,
    copyLabel,
    copiedLabel,
  } = options;
  let tableIndex = 0;

  const components: Components = {
    p: ({ children }) => <div className="restaurant-chatbot-text">{children}</div>,
    h1: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    h2: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    h3: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    h4: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    h5: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    h6: ({ children }) => <div className="restaurant-chatbot-markdown-heading">{children}</div>,
    a: ({ href, children }) => (
      <a href={href} target={EXTERNAL_LINK_TARGET} rel={EXTERNAL_LINK_REL}>
        {children}
      </a>
    ),
    img: ({ src, alt }) => {
      const imageSrc = src ?? "";
      const imageAlt = alt ?? "Generated image";
      const imageStarState = options.getImageStarState?.(imageSrc, imageAlt);
      const imageLineState = options.getImageLineState?.(imageSrc, imageAlt);

      return (
        <MarkdownImage
          src={imageSrc}
          alt={imageAlt}
          onOpen={onOpenImageViewer}
          onCopy={onCopyImage}
          onDownload={onDownloadImage}
          canStar={imageStarState?.canStar}
          isStarred={imageStarState?.isStarred}
          onToggleStar={imageStarState?.onToggleStar}
          canSendToLine={imageLineState?.canSend}
          onSendToLine={imageLineState?.onSend}
        />
      );
    },
    // `node` is the mdast node react-markdown passes to every component.
    // node.position.start/end.offset are character offsets into the ORIGINAL
    // markdown string, so we can just slice it out — no re-parsing needed.
    table: ({ children, node }) => {
      const currentTableIndex = tableIndex;
      tableIndex += 1;
      const tableId = `${messageId}-${TABLE_COPY_ID_SEGMENT}-${currentTableIndex}`;
      const isCopied = copiedId === tableId;

      const position = node?.position;
      const tableMarkdown = position
        ? content.slice(position.start.offset, position.end.offset)
        : "";

      return (
        <div className="restaurant-chatbot-table-block">
          {onCopyTable && tableMarkdown ? (
            <div className={`restaurant-chatbot-table-actions${isCopied ? COPIED_CLASS_NAME : ""}`}>
              <button
                className={`restaurant-chatbot-copy-btn${isCopied ? COPIED_CLASS_NAME : ""}`}
                type={BUTTON_TYPE}
                onClick={() => onCopyTable(tableId, tableMarkdown)}
                title={isCopied ? copiedLabel : copyLabel}
              >
                <i className={isCopied ? COPIED_ICON_CLASS_NAME : COPY_ICON_CLASS_NAME} />
              </button>
            </div>
          ) : null}
          <div className="restaurant-chatbot-table-wrapper">
            <table className="restaurant-chatbot-table">{children}</table>
          </div>
        </div>
      );
    },
  };

  return [
    <ReactMarkdown key="chatbot-markdown" remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>,
  ];
};
